/**
 * O formulário do plano, sem tela (#766): o que a pessoa digitou vira o pedido
 * ao servidor, ou o motivo de não virar, campo a campo.
 *
 * A conferência daqui é a de formato. Nome repetido e o resto das regras do
 * catálogo são do servidor, que responde com o motivo.
 *
 * Na edição vai só o que mudou. Valor que já existia não pode ficar vazio: o
 * plano legado ganha preço, mas nenhum plano perde o que tem.
 */

import type { CriarPlano, EditarPlano } from '@cliqcrm/contracts';
import { emMicros } from '@/shared/lib/formato';
import type { Dinheiro, PlanoDoCatalogo } from '../model/plano-do-catalogo';

export type CamposDoPlano = {
  readonly nome: string;
  readonly mensalidade: string;
  readonly usuariosIncluidos: string;
  readonly armazenamentoGb: string;
  readonly usuarioAdicional: string;
  readonly gbAdicional: string;
  readonly observacao: string;
  readonly ativo: boolean;
};

export type ErrosDoPlano = Partial<Record<keyof CamposDoPlano, string>>;

export type LeituraDoPlano<Corpo> =
  | { readonly ok: true; readonly corpo: Corpo }
  | { readonly ok: false; readonly erros: ErrosDoPlano };

export const LIMITE_DO_NOME = 80;
export const LIMITE_DA_OBSERVACAO = 280;
/** O maior número que o banco guarda em usuários e em GB (`integer`). */
const MAIOR_INTEIRO = 2_147_483_647;

/** O plano novo começa com os 10 GB que o catálogo usa como padrão. */
export const CAMPOS_DE_PLANO_NOVO: CamposDoPlano = {
  nome: '',
  mensalidade: '',
  usuariosIncluidos: '',
  armazenamentoGb: '10',
  usuarioAdicional: '',
  gbAdicional: '',
  observacao: '',
  ativo: true,
};

const OBRIGATORIO = 'Preencha este campo.';
const INTEIRO = 'Escreva um número inteiro, a partir de 1.';
const DINHEIRO = 'Escreva o valor em reais, como 49,90.';
const NAO_APAGA = 'Este valor já existe e não pode ficar vazio.';
const ACIMA_DO_TETO = 'No máximo 2.147.483.647.';
const PARA_ATIVAR = 'Preencha este campo para ativar o plano.';

const MOEDA = 'BRL';

/** "49,90" a partir do valor; vazio quando o plano legado não tem o valor. */
export function textoDeReais(valor: Dinheiro | null): string {
  if (!valor) return '';
  return (valor.quantia_micros / 1_000_000).toFixed(2).replace('.', ',');
}

/** Os campos de um plano que já existe, para editar. */
export function camposDoPlano(plano: PlanoDoCatalogo): CamposDoPlano {
  return {
    nome: plano.nome,
    mensalidade: textoDeReais(plano.mensalidade),
    usuariosIncluidos: plano.usuariosIncluidos === null ? '' : String(plano.usuariosIncluidos),
    armazenamentoGb: String(plano.armazenamentoGb),
    usuarioAdicional: textoDeReais(plano.usuarioAdicional),
    gbAdicional: textoDeReais(plano.gbAdicional),
    observacao: plano.observacao ?? '',
    ativo: plano.ativo,
  };
}

function inteiro(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^\d+$/.test(limpo)) return null;
  const numero = Number(limpo);
  return numero >= 1 && numero <= MAIOR_INTEIRO ? numero : null;
}

/** Por que o número não serve: abaixo de 1 (ou não é número) e acima do teto têm frases diferentes. */
function motivoDoInteiro(texto: string): string {
  const limpo = texto.trim();
  return /^\d+$/.test(limpo) && Number(limpo) > MAIOR_INTEIRO ? ACIMA_DO_TETO : INTEIRO;
}

/**
 * O plano pode ficar ativo com o que está nos campos? Plano ativo aparece para
 * organização nova, que pede os usuários e os preços; o servidor recusa do
 * mesmo jeito, mas a tela diz antes.
 */
export function podeAtivar(campos: CamposDoPlano): boolean {
  return (['mensalidade', 'usuariosIncluidos', 'usuarioAdicional', 'gbAdicional'] as const).every(
    (campo) => campos[campo].trim() !== '',
  );
}

function reais(texto: string): { quantia_micros: number; moeda: 'BRL' } | null {
  const micros = emMicros(texto);
  return micros === null ? null : { quantia_micros: micros, moeda: MOEDA };
}

function erroDoNome(nome: string): string | undefined {
  const limpo = nome.trim();
  if (limpo === '') return OBRIGATORIO;
  if (limpo.length > LIMITE_DO_NOME) return `Use no máximo ${LIMITE_DO_NOME} caracteres.`;
  return undefined;
}

function erroDaObservacao(observacao: string): string | undefined {
  return observacao.trim().length > LIMITE_DA_OBSERVACAO
    ? `Use no máximo ${LIMITE_DA_OBSERVACAO} caracteres.`
    : undefined;
}

const INTEIROS = ['usuariosIncluidos', 'armazenamentoGb'] as const;
const VALORES = ['mensalidade', 'usuarioAdicional', 'gbAdicional'] as const;

/** O plano novo: tudo obrigatório, menos a observação. */
export function lerPlanoNovo(campos: CamposDoPlano): LeituraDoPlano<CriarPlano> {
  const erros: { -readonly [C in keyof CamposDoPlano]?: string } = {};
  const nome = erroDoNome(campos.nome);
  if (nome) erros.nome = nome;
  for (const campo of INTEIROS) {
    if (campos[campo].trim() === '') erros[campo] = OBRIGATORIO;
    else if (inteiro(campos[campo]) === null) erros[campo] = motivoDoInteiro(campos[campo]);
  }
  for (const campo of VALORES) {
    if (campos[campo].trim() === '') erros[campo] = OBRIGATORIO;
    else if (reais(campos[campo]) === null) erros[campo] = DINHEIRO;
  }
  const observacao = erroDaObservacao(campos.observacao);
  if (observacao) erros.observacao = observacao;

  if (Object.keys(erros).length > 0) return { ok: false, erros };
  return {
    ok: true,
    corpo: {
      nome: campos.nome.trim(),
      mensalidade: reais(campos.mensalidade)!,
      usuarios_incluidos: inteiro(campos.usuariosIncluidos)!,
      armazenamento_gb: inteiro(campos.armazenamentoGb)!,
      usuario_adicional: reais(campos.usuarioAdicional)!,
      gb_adicional: reais(campos.gbAdicional)!,
      observacao: campos.observacao.trim() === '' ? null : campos.observacao.trim(),
    },
  };
}

/** A edição mudou alguma coisa? Sem mudança, não há o que salvar. */
export function mudouAlgo(original: CamposDoPlano, campos: CamposDoPlano): boolean {
  return (Object.keys(original) as (keyof CamposDoPlano)[]).some((campo) =>
    typeof original[campo] === 'string'
      ? (original[campo] as string).trim() !== (campos[campo] as string).trim()
      : original[campo] !== campos[campo],
  );
}

const NOME_NO_CONTRATO = {
  usuariosIncluidos: 'usuarios_incluidos',
  armazenamentoGb: 'armazenamento_gb',
  mensalidade: 'mensalidade',
  usuarioAdicional: 'usuario_adicional',
  gbAdicional: 'gb_adicional',
} as const;

/** A edição: vai só o que mudou, com a mesma conferência do plano novo. */
export function lerEdicaoDoPlano(
  original: CamposDoPlano,
  campos: CamposDoPlano,
): LeituraDoPlano<EditarPlano> {
  const erros: { -readonly [C in keyof CamposDoPlano]?: string } = {};
  const corpo: Record<string, unknown> = {};
  const mudou = (campo: keyof CamposDoPlano) =>
    (original[campo] as string).trim() !== (campos[campo] as string).trim();

  if (mudou('nome')) {
    const erro = erroDoNome(campos.nome);
    if (erro) erros.nome = erro;
    else corpo['nome'] = campos.nome.trim();
  }
  for (const campo of INTEIROS) {
    if (!mudou(campo)) continue;
    const numero = inteiro(campos[campo]);
    if (campos[campo].trim() === '')
      erros[campo] = original[campo].trim() === '' ? INTEIRO : NAO_APAGA;
    else if (numero === null) erros[campo] = motivoDoInteiro(campos[campo]);
    else corpo[NOME_NO_CONTRATO[campo]] = numero;
  }
  for (const campo of VALORES) {
    if (!mudou(campo)) continue;
    const valor = reais(campos[campo]);
    if (campos[campo].trim() === '') erros[campo] = NAO_APAGA;
    else if (valor === null) erros[campo] = DINHEIRO;
    else corpo[NOME_NO_CONTRATO[campo]] = valor;
  }
  if (mudou('observacao')) {
    const erro = erroDaObservacao(campos.observacao);
    if (erro) erros.observacao = erro;
    else corpo['observacao'] = campos.observacao.trim() === '' ? null : campos.observacao.trim();
  }
  if (original.ativo !== campos.ativo) corpo['ativo'] = campos.ativo;
  // Ligou o ativo e depois esvaziou um número: o campo vazio diz o que falta,
  // em vez de o servidor recusar sem apontar o campo.
  if (campos.ativo && !original.ativo) {
    for (const campo of [
      'mensalidade',
      'usuariosIncluidos',
      'usuarioAdicional',
      'gbAdicional',
    ] as const) {
      if (campos[campo].trim() === '') erros[campo] ??= PARA_ATIVAR;
    }
  }

  if (Object.keys(erros).length > 0) return { ok: false, erros };
  return { ok: true, corpo: corpo as EditarPlano };
}
